import React, { useEffect, useRef, useState } from "react";
import { cn } from "../../../lib/utils";
import GlassSurface, { GlassVariant } from "./GlassSurface";
import type {
  GlassShape,
  LiquidGlassEngine,
  LiquidGlassOptions,
} from "../../../lib/liquid-glass/engine";

export interface LiquidGlassProps extends LiquidGlassOptions {
  /** Optional backdrop image URL to refract. Defaults to a navy brand wash. */
  backdropImage?: string;
  /** CSS border-radius for the box (rounded shape). Defaults from `radius`. */
  className?: string;
  style?: React.CSSProperties;
  /** Variant used when WebGL is unavailable and we fall back to CSS glass. */
  fallbackVariant?: GlassVariant;
  /** Class applied to the inner content layer (carry flex/grid layout here). */
  contentClassName?: string;
  /** Called once the WebGL engine is created — use it to drive setLight()
   *  from tilt/pointer. Pass a STABLE callback (useCallback). */
  onEngineReady?: (engine: LiquidGlassEngine) => void;
  /** Whether to run the living-light animation loop. Defaults to true. Set to
   *  false for static glass surfaces (e.g. sticky section bars inside a sheet)
   *  so they get the same WebGL rim/sheen/refraction as the sheet without the
   *  cost of an extra continuous RAF loop. */
  animated?: boolean;
  children?: React.ReactNode;
}

/** Cheap one-shot WebGL2 capability probe (no engine import needed). */
let webgl2Cached: boolean | null = null;
function supportsWebGL2(): boolean {
  if (webgl2Cached !== null) return webgl2Cached;
  if (typeof document === "undefined") return (webgl2Cached = false);
  try {
    webgl2Cached = !!document.createElement("canvas").getContext("webgl2");
  } catch {
    webgl2Cached = false;
  }
  return webgl2Cached;
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    !!window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

/** Procedural navy/ice backdrop so the refraction always has something to
 *  bend, even without a supplied image. Sized to the element in device px. */
function buildBackdrop(w: number, h: number): HTMLCanvasElement {
  const cv = document.createElement("canvas");
  cv.width = Math.max(1, w);
  cv.height = Math.max(1, h);
  const ctx = cv.getContext("2d")!;
  const g = ctx.createLinearGradient(0, 0, w, h);
  g.addColorStop(0, "#0a2f5a");
  g.addColorStop(0.55, "#01183a");
  g.addColorStop(1, "#010e24");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, w, h);
  // Soft ice glints — give the displacement something to refract.
  const glint = (cx: number, cy: number, r: number, a: number) => {
    const rg = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
    rg.addColorStop(0, `rgba(148,194,220,${a})`);
    rg.addColorStop(1, "rgba(148,194,220,0)");
    ctx.fillStyle = rg;
    ctx.fillRect(0, 0, w, h);
  };
  glint(w * 0.18, h * 0.12, Math.max(w, h) * 0.5, 0.45);
  glint(w * 0.85, h * 0.78, Math.max(w, h) * 0.42, 0.28);
  return cv;
}

/**
 * LiquidGlass — the real-WebGL "ice" hero, ~once per product.
 *
 * Gated: needs WebGL2. When unavailable it renders <GlassSurface> (the CSS
 * glass) with matching geometry, so consumers can drop it anywhere safely.
 * The engine is dynamically imported, keeping WebGL out of the main bundle
 * until a hero actually mounts. Under prefers-reduced-motion it renders a
 * single static frame instead of animating.
 */
const LiquidGlass: React.FC<LiquidGlassProps> = ({
  backdropImage,
  className,
  style,
  fallbackVariant = "panel",
  contentClassName,
  onEngineReady,
  animated = true,
  children,
  shape = "rounded",
  radius = 28,
  ...engineOpts
}) => {
  const supported = supportsWebGL2();
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!supported || failed) return;
    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    if (!wrap || !canvas) return;

    let engine: LiquidGlassEngine | null = null;
    let ro: ResizeObserver | null = null;
    let cancelled = false;
    let backdropImg: HTMLImageElement | null = null;
    const reduced = prefersReducedMotion();
    const animate = animated && !reduced;

    const applyBackdrop = (w: number, h: number) => {
      if (!engine) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      if (backdropImg && backdropImg.complete && backdropImg.naturalWidth) {
        engine.setBackdrop(backdropImg);
      } else {
        engine.setBackdrop(
          buildBackdrop(Math.round(w * dpr), Math.round(h * dpr))
        );
      }
    };

    (async () => {
      try {
        const mod = await import("../../../lib/liquid-glass/engine");
        if (cancelled) return;
        engine = new mod.LiquidGlassEngine(canvas, {
          shape: shape as GlassShape,
          radius,
          ...engineOpts,
        });
        onEngineReady?.(engine);

        const sync = () => {
          if (!engine || !wrap) return;
          const r = wrap.getBoundingClientRect();
          engine!.resize(r.width, r.height);
          applyBackdrop(r.width, r.height);
          if (!animate) engine!.renderOnce();
        };

        if (backdropImage) {
          backdropImg = new Image();
          backdropImg.crossOrigin = "anonymous";
          backdropImg.onload = () => {
            const r = wrap.getBoundingClientRect();
            applyBackdrop(r.width, r.height);
          };
          backdropImg.src = backdropImage;
        }

        sync();
        if (animate) engine.startLoop();

        ro = new ResizeObserver(() => sync());
        ro.observe(wrap);
      } catch (e) {
        if (!cancelled) {
          // eslint-disable-next-line no-console
          console.warn("[LiquidGlass] WebGL init failed, using CSS glass", e);
          setFailed(true);
        }
      }
    })();

    return () => {
      cancelled = true;
      ro?.disconnect();
      engine?.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supported, failed, backdropImage, shape, radius]);

  const borderRadius =
    shape === "circle" || shape === "pill" ? 9999 : radius;

  if (!supported || failed) {
    return (
      <GlassSurface
        variant={fallbackVariant}
        refract
        className={className}
        style={{ borderRadius, ...style }}
      >
        <div className={contentClassName}>{children}</div>
      </GlassSurface>
    );
  }

  return (
    <div
      ref={wrapRef}
      className={cn("bb-liquid", className)}
      style={{ borderRadius, ...style }}
    >
      <canvas ref={canvasRef} className="bb-liquid__canvas" />
      <div className={cn("bb-liquid__content", contentClassName)}>{children}</div>
    </div>
  );
};

export default LiquidGlass;
